import { Link, useNavigate } from 'react-router-dom';
import { SquareArrowOutUpRightIcon, Trash2Icon, VibrateIcon } from "lucide-react";
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg';
import Modal from 'components/Modal/Modal';
import { ClientInterface, NotificationKey, NotificationMap, NotificationProp, notificationTypes } from 'functions/hooks/useGetUsers';
import EditClientForm from 'pages/Agent/Onboarding/Forms/EditClientForm';
import EditDriverForm from 'pages/Agent/Onboarding/Forms/EditDriverForm';
import { DriverInterface } from 'types/DriverInterface';
import { VehicleInterface } from 'types/VehicleInterface';

import './InfoCard.scss';
import EditVehicleForm from 'pages/Agent/Onboarding/Forms/EditVehicleForm';
import { MouseEventHandler, useRef, useState } from 'react';
import { Func } from 'mocha';
import { JsxElement } from 'typescript';
import Button from 'components/Button/Button';
import CheckBox from 'components/CheckBox/CheckBox';
import axios from 'axios';
import { useAppSettings } from 'context/appsettingsContext';
import { useAuth } from 'context/useAuth';
import { successAsync, errorAsync } from 'functions/notification';
export interface InfoCardProps {
  /** Info card client details */
  clientDetails?: ClientInterface;
  /** Info card driver details */
  driverDetails?: DriverInterface;
  /** Info card car details */
  vehicleDetails?: VehicleInterface;
  /** Info card title */
  title?: string;
  /** Info card subtitle */
  subtitle?: string;
  /** Info card single field to go with subtitle */
  field?: string;
  /** Info card single field to go with subtitle */
  clientId?: string;
  /** Delete Event **/
  onDelete?: MouseEventHandler | undefined,
  /** User Update event **/
  userUpdated?: (clientDetails: ClientInterface) => any | Promise<any>,
  /** Vehicle Update event **/
  vehicleUpdated?: (vehicleDetails: VehicleInterface) => any | Promise<any>,
}

function renderField(
  label: string,
  value: string | number,
  fullSize?: boolean
): React.JSX.Element {
  let linkPrefix = '';
  if (label === 'Email address') {
    linkPrefix = 'mailto:';
  } else if (label === 'Cellphone number') {
    linkPrefix = 'tel:';
  }

  return (
    <div
      className={`info-card__field ${fullSize ? 'info-card__field-full' : ''}`}
    >
      <div className='info-card__field-title'>{label}</div>
      {linkPrefix ? (
        <a
          href={`${linkPrefix}${value}`}
          className='info-card__link'
          aria-label={`info card ${label}`}
        >
          {value || '-'}
        </a>
      ) : (
        <div>{value || '-'}</div>
      )}
    </div>
  );
}

function renderElement(
  label: string,
  element?: React.JSX.Element
): React.JSX.Element {
  return (
    <div className={`info-card__field`}>
       <div className='info-card__field-title'>{label}</div>
       <div className='cursor-pointer'>{element}</div>
    </div>
  );
}

/** UI component containing user information */
export default function InfoCard({
  title,
  subtitle,
  field,
  clientDetails,
  driverDetails,
  vehicleDetails,
  clientId,
  onDelete,
  userUpdated,
  vehicleUpdated
}: InfoCardProps) {

  const navigate = useNavigate();
  const settings = useAppSettings();
  const { authorisedUser } = useAuth();
  const notificationModalRef = useRef();
  const [isSavingNotification, setIsSavingNotification] = useState(false);
  const [notificationDefaults] = useState<NotificationMap>(clientDetails?.notification ?? {} as any);
  const [notification, setNotification] = useState<NotificationMap>(clientDetails?.notification ?? {} as any);

  function onNotificationCancel() {
    setTimeout(() => setNotification(notificationDefaults), 1000);
  }

  async function onNotificationSave() {
    
    setIsSavingNotification(true);

    try {

      const requestData = {
        ...clientDetails,
        userId: clientDetails?.id,
        notification: notification
      };

      await axios.post<any, any>(settings.updateUserUrl, requestData, { headers: { Authorization: `Bearer ${authorisedUser.accessToken}` } });

      successAsync("Saved");

      if (userUpdated) {
        userUpdated(Object.assign(clientDetails!, requestData))
      }

      (notificationModalRef.current as any)?.close();
      
    } catch (error: any) {
      errorAsync(`Failed to save : ${error?.response?.data?.message || error?.message || error}`, error);
    } finally {
      setIsSavingNotification(false);
    }
  }

  return (
    <div>
      <div className='flex align-items-center justify-content-space-between'>
        {title && (
          <div className='info-card__title' aria-label='Info card title'>
            {title}
          </div>
        )}

        {clientDetails && (
            <>
                <Link to={'#'} aria-label='client delete icon' className='info-card__icon' style={{backgroundColor:'#F08080', color:'white'}} onClick={onDelete}>
                    <Trash2Icon />
                </Link>
                <Modal title='Account Holder Details' trigger={<Link to={'#'} aria-label='client edit icon' className='info-card__icon'><EditIcon /></Link>}>
                    <EditClientForm userDetails={clientDetails} userUpdated={details => userUpdated ? userUpdated(Object.assign(clientDetails, details)) : null} />
                </Modal>
            </>
        )}

        {/**driverDetails && (
          <Modal
            title='Driver details'
            trigger={
              <Link
                to={'#'}
                className='info-card__icon'
                aria-label='driver edit icon'
              >
                <EditIcon />
              </Link>
            }
          >
            <EditDriverForm userDetails={driverDetails} />
          </Modal>
        )**/}

        {vehicleDetails && (
            <>
                <a style={{cursor:'pointer',display:vehicleDetails.deviceId?'block':'none'}} onClick={() => navigate(`/agent/mgt-devices/${vehicleDetails.deviceId}?fromclientid=${clientId}`)} title='Go to Device'>
                    <SquareArrowOutUpRightIcon/>
                </a>
                <Modal title='Vehicle details' trigger={<Link to={'#'} aria-label='vehicle edit icon' className='info-card__icon'><EditIcon /></Link>}>
                    <EditVehicleForm vehicleDetails={vehicleDetails} vehicleUpdated={details => vehicleUpdated ? vehicleUpdated(Object.assign(vehicleDetails, details)) : null} />
                </Modal>
            </>
            // <div className='info-card__button-wrapper'>
            //   <Link
            //     aria-label='view history'
            //     to={`/agent/clients/${vehicleDetails.userId}/vehicle/${vehicleDetails.id}/history`}
            //     className='info-card__icon'
            //     style={{ display: 'none' }}
            //   >
            //     <HistoryIcon />
            //   </Link>
            // </div>
        )}
      </div>

      <div className='info-card'>
        {subtitle && (
          <div>
            <div
              className='info-card__subtitle'
              aria-label='Info card subtitle'
            >
              {subtitle}
            </div>

            <div>{field}</div>
          </div>
        )}

        {clientDetails && (
          <div className='info-card__fields'>
            {renderField('Email Address', clientDetails.email)}
            {renderField('Cellphone Number', (clientDetails.phoneCode && clientDetails.phoneNumber) ? ('+' + clientDetails.phoneCode + clientDetails.phoneNumber) : '')}
            {renderField('ID Number', clientDetails.idNumber)}
            {renderField('AURA', clientDetails.auraDisabled ? 'Disabled' : 'Enabled')}
            {renderField('3DT', clientDetails.threeDtPackage ? clientDetails.threeDtPackage : 'Disabled')}
            {renderElement('Notification', <Modal title='Notification' ref={notificationModalRef} onClose={onNotificationCancel} trigger={<Link to={'#'} aria-label='notification edit icon'><VibrateIcon /></Link>}>
                <div style={{ marginBottom: '0.5em' }}>
                    <Button small label="Enable all" onClick={() => setNotification(n => Object.fromEntries(Object.keys(n).filter(k => typeof n[k as NotificationProp] === 'boolean').map(k => [k, true])) as NotificationMap)} />
                    <Button small label="Disable all" style={{ marginLeft: '1em' }} onClick={() => setNotification(n => Object.fromEntries(Object.keys(n).filter(k => typeof n[k as NotificationProp] === 'boolean').map(k => [k, false])) as NotificationMap)} />
                </div>
                <div className='notification-grid'>
                    {(Object.entries(notificationTypes) as [NotificationKey, string][]).map(([key, label]) => {
                        const pushKey = `${key}Push` as NotificationProp;
                        const smsKey  = `${key}Sms`  as NotificationProp;
                        return (
                            <div key={key} className="fieldset-box" data-label={label}>
                                <CheckBox id={pushKey} name={pushKey} label="Push" checked={!!notification[pushKey]} onChange={e => setNotification(n => ({ ...n, [e.target.name]: e.target.checked }))} />
                                <CheckBox id={smsKey}  name={smsKey}  label="SMS"  checked={!!notification[smsKey]}  onChange={e => setNotification(n => ({ ...n, [e.target.name]: e.target.checked }))} />
                            </div>
                        );
                    })}
                </div>
                <div className='form-actions'>
                    <Button small label={isSavingNotification ? 'Saving' : 'Save'} disabled={isSavingNotification} onClick={onNotificationSave} />
                </div>
            </Modal>)}
          </div>
        )}

        {driverDetails && (
          <div className='info-card__fields'>
            {renderField('Email Address', driverDetails.email)}
            {renderField('Cellphone Number', (driverDetails.phoneCode && driverDetails.phoneNumber) ? ('+' + driverDetails.phoneCode + driverDetails.phoneNumber) : '')}
            {renderField('ID Number', driverDetails.idNumber)}
          </div>
        )}

        {vehicleDetails && (
          <div className='info-card__fields'>
            {renderField('Make', vehicleDetails.make)}
            {renderField('Model', vehicleDetails.model)}
            {renderField('Year', vehicleDetails.registrationYear)}
            {renderField('Color', vehicleDetails.vehicleColour)}
            {renderField('VIN Number', vehicleDetails.vinNumber)}
            {renderField('Engine Number', vehicleDetails.engineNumber)}
            {renderField('Device ID', vehicleDetails.deviceId)}
            {renderField('AURA', vehicleDetails.auraDisabled ? 'Disabled' : 'Enabled')}
            {renderField('3DT', vehicleDetails.threeDtCode ? vehicleDetails.threeDtCode : 'Disabled')}
          </div>
        )}
      </div>
    </div>
  );
}
